<template>
  <div class="min-h-screen bg-gray-50">
    <Head title="Tasas de cambio" />
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center space-x-4">
          <h1 class="text-2xl font-bold text-gray-900">Tasas de cambio</h1>
          <span class="px-3 py-1 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
            {{ changes.data.length }} registros
          </span>
        </div>
        <Link 
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          href="/taxes/create"
        >
          <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nueva tasa de cambio
        </Link>
      </div>

      <!-- BCV Real-time Rates Banner/Card -->
      <div class="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div class="flex items-center space-x-3">
          <span class="p-2 bg-indigo-100 text-indigo-700 rounded-full">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </span>
          <div>
            <h2 class="text-sm font-semibold text-indigo-950">Consulta de Tasas Oficiales en BCV</h2>
            <p class="text-xs text-indigo-700">Consulta las tasas actuales directamente desde el Banco Central de Venezuela y regístralas al instante.</p>
          </div>
        </div>
        
        <div class="flex items-center gap-3">
          <div v-if="bcvRates.usd || bcvRates.eur" class="flex flex-wrap items-center gap-4 mr-2">
            <div v-if="bcvRates.usd" class="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-md border border-indigo-200 shadow-sm">
              <span class="text-xs font-semibold text-gray-500">USD:</span>
              <span class="text-sm font-bold text-gray-900">{{ formatCurrency(bcvRates.usd) }} Bs</span>
              <button 
                @click="scrapeRate('usd')" 
                :disabled="scraping"
                class="text-xs text-indigo-600 hover:text-indigo-950 font-semibold underline disabled:opacity-50 transition-colors duration-200"
              >
                Registrar
              </button>
            </div>
            <div v-if="bcvRates.eur" class="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-md border border-indigo-200 shadow-sm">
              <span class="text-xs font-semibold text-gray-500">EUR:</span>
              <span class="text-sm font-bold text-gray-900">{{ formatCurrency(bcvRates.eur) }} Bs</span>
              <button 
                @click="scrapeRate('eur')" 
                :disabled="scraping"
                class="text-xs text-indigo-600 hover:text-indigo-950 font-semibold underline disabled:opacity-50 transition-colors duration-200"
              >
                Registrar
              </button>
            </div>
          </div>
          
          <button 
            type="button"
            @click="fetchBcvRates"
            :disabled="loadingBcv"
            class="inline-flex items-center px-4 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 shadow-sm transition-colors duration-200"
          >
            <svg v-if="loadingBcv" class="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-700" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {{ loadingBcv ? 'Buscando...' : 'Buscar Tasa en BCV' }}
          </button>
        </div>
      </div>

      <!-- Table Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tasa de cambio
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha de creación
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última actualización
                </th>
                <th scope="col" class="relative px-6 py-3">
                  <span class="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="change in changes.data" :key="change.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  #{{ change.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">
                    {{ formatCurrency(change.change) }} Bs
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(change.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(change.updated_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link 
                    :href="`/taxes/${change.id}/edit`"
                    class="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                  >
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                </td>
              </tr>
              <tr v-if="!changes.data.length">
                <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                  No hay tasas de cambio registradas
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-6">
        <pagination :links="changes.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    changes: Object
  },
  data() {
    return {
      scraping: false,
      scrapingType: null,
      loadingBcv: false,
      bcvRates: {
        usd: null,
        eur: null
      }
    }
  },
  methods: {
    scrapeRate(type) {
      this.scraping = true
      this.scrapingType = type
      this.$inertia.post('/taxes/scrape', { type }, {
        onFinish: () => {
          this.scraping = false
          this.scrapingType = null
        }
      })
    },
    fetchBcvRates() {
      this.loadingBcv = true
      Promise.all([
        fetch('/taxes/rate?type=usd').then(r => {
          if (!r.ok) throw new Error()
          return r.json()
        }),
        fetch('/taxes/rate?type=eur').then(r => {
          if (!r.ok) throw new Error()
          return r.json()
        })
      ])
      .then(([usdData, eurData]) => {
        if (usdData.rate) this.bcvRates.usd = usdData.rate
        if (eurData.rate) this.bcvRates.eur = eurData.rate
        if (!usdData.rate && !eurData.rate) {
          alert('No se pudieron obtener las tasas desde el BCV.')
        }
      })
      .catch(error => {
        console.error(error)
        alert('Hubo un error al conectar con el servidor para buscar las tasas.')
      })
      .finally(() => {
        this.loadingBcv = false
      })
    },
    formatCurrency(value) {
      const num = parseFloat(value)
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      }).format(num)
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}
</style>
  